/**
 * Native date/time picker opener.
 *
 * Desktop Chrome/Edge open the native calendar of <input type="date|datetime-local|time|month|week">
 * only when the small icon at the right edge is clicked — a click on the text part only focuses
 * a segment (dd / mm / yyyy). Users expect the whole field to open the calendar, so a click anywhere
 * in such a field calls HTMLInputElement.showPicker(). Keyboard entry keeps working: the focused
 * segment still accepts typed digits while the picker is open.
 *
 * Skipped:
 * - readonly and disabled fields (the browser would refuse anyway),
 * - coarse pointers (phones, tablets) — a tap already opens the native picker there,
 * - fields inside an element with data-dcms-native-picker="off" (opt-out for custom widgets),
 * - clicks whose default action was prevented by another handler.
 *
 * One delegated listener per window. The React package (@dcms2/react-ui, DateTimePicker) installs
 * the same listener under the same window flag, so a page loading both bundles gets it once.
 */

const NATIVE_PICKER_TYPES = new Set(['date', 'datetime-local', 'time', 'month', 'week']);
const INSTALLED_FLAG = '__dcmsNativePickerOpener';

function onNativePickerClick(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !NATIVE_PICKER_TYPES.has(input.type)) {
        return;
    }
    if (event.defaultPrevented || input.readOnly || input.disabled || typeof input.showPicker !== 'function') {
        return;
    }
    if (input.closest('[data-dcms-native-picker="off"]')) {
        return;
    }
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
        return;
    }
    try {
        input.showPicker();
    } catch (e) {
        // NotAllowedError (no user activation) / SecurityError (cross-origin frame): keep the default behaviour
    }
}

export function installNativePickerOpener() {
    if (typeof window === 'undefined' || window[INSTALLED_FLAG]) {
        return;
    }
    window[INSTALLED_FLAG] = true;
    document.addEventListener('click', onNativePickerClick);
}

installNativePickerOpener();
